import React, { useState, useEffect } from 'react';
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';

const AdminLayout = () => {
  const [user, setUser] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Check if logged in
    const token = localStorage.getItem('adminToken');
    const storedUser = localStorage.getItem('adminUser');

    if (!token || !storedUser) {
      navigate('/admin/login');
      return;
    }

    setUser(JSON.parse(storedUser));

    // Verify token
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/verify`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }).then(res => {
      if (!res.ok) {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
        navigate('/admin/login');
      }
    }).catch(() => {
      navigate('/admin/login');
    });
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    navigate('/admin/login');
  };

  const isActive = (path) => location.pathname === path;

  const navItems = [
    { path: '/admin/dashboard', label: 'Dashboard', icon: '📊' },
    { path: '/admin/specials', label: 'Specials & Pricing', icon: '🎁' },
    { path: '/admin/homepage', label: 'Homepage', icon: '🏠' },
    { path: '/admin/press-releases', label: 'Press Releases', icon: '📰' },
    { path: '/admin/blog', label: 'Blog (RSS)', icon: '✍️' },
    { path: '/admin/gbp', label: 'GBP Automation', icon: '🚀' },
    { path: '/admin/gbp-qa', label: 'GBP Q&A Monitor', icon: '❓' },
    { path: '/admin/reviews', label: 'Review Generation', icon: '⭐' },
    { path: '/admin/review-responses', label: 'Review Responses', icon: '💬' },
    { path: '/admin/citation-builder', label: 'Citation Builder', icon: '🏗️' },
    { path: '/admin/service-areas', label: 'Service Areas', icon: '🗺️' },
    { path: '/admin/link-building', label: 'Link Building', icon: '🔗' },
    { path: '/admin/competitors', label: 'Competitors', icon: '🕵️' },
    { path: '/admin/seo-intelligence', label: 'SEO Intelligence', icon: '🧠' },
    { path: '/admin/voting-reminder', label: 'Voting Reminder', icon: '🗳️' },
    { path: '/admin/chat-logs', label: 'Chat Widget Logs', icon: '💬' },
    { path: '/admin/syndication', label: 'Backlinks', icon: '🔗' },
    { path: '/admin/ab-tests', label: 'A/B Tests', icon: '🧪' },
    { path: '/admin/analytics', label: 'A/B Analytics', icon: '📈' },
    { path: '/admin/ga4-analytics', label: 'GA4 Analytics', icon: '📊' },
    { path: '/admin/indexnow', label: 'IndexNow', icon: '⚡' },
    { path: '/admin/google-indexing', label: 'Google Indexing', icon: '🔍' },
    { path: '/admin/roi-dashboard', label: 'ROI Dashboard', icon: '📊' },
    { path: '/admin/data-sync', label: 'Data Sync', icon: '🔄' },
    { path: '/admin/bilingual', label: 'Bilingual ES', icon: '🌐' },
    { path: '/admin/crm', label: 'CRM', icon: '📨' },
    { path: '/admin/users', label: 'Admin Users', icon: '👥' },
    { path: '/admin/social-webhooks', label: 'Social Auto-Post', icon: '⚡' },
    { path: '/admin/redirects', label: '301 Redirects', icon: '↪️' },
  ];

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gray-100 flex">
      <title>Admin Dashboard | Fort Worth Dental</title>
      <meta name="robots" content="noindex, nofollow" />

      {/* Sidebar */}
      <aside className={`${sidebarOpen ? 'w-64' : 'w-16'} bg-gray-800 text-white transition-all duration-300 flex flex-col min-h-screen`}>
        <div className="p-4">
          <div className="flex items-center justify-between">
            {sidebarOpen && <h2 className="text-lg font-bold">FWD Admin</h2>}
            <button 
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 hover:bg-gray-700 rounded"
            >
              {sidebarOpen ? '←' : '→'}
            </button>
          </div>
        </div>

        <nav className="mt-4 flex-1 overflow-y-auto">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center px-4 py-3 hover:bg-gray-700 transition ${
                isActive(item.path) ? 'bg-teal-600' : ''
              }`}
            >
              <span className="text-xl">{item.icon}</span>
              {sidebarOpen && <span className="ml-3">{item.label}</span>}
            </Link>
          ))}
        </nav>

        <div className="p-4 border-t border-gray-700">
          <button
            onClick={handleLogout}
            className="flex items-center w-full px-4 py-2 text-red-400 hover:bg-gray-700 rounded"
          >
            <span>🚪</span>
            {sidebarOpen && <span className="ml-3">Logout</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        {/* Top Bar */}
        <header className="bg-white shadow-sm px-6 py-4 flex justify-between items-center">
          <h1 className="text-xl font-semibold text-gray-800">
            {navItems.find(item => isActive(item.path))?.label || 'Admin'}
          </h1>
          <div className="flex items-center space-x-4">
            <span className="text-gray-600">Welcome, {user.name}</span>
            <Link to="/" target="_blank" className="text-teal-600 hover:underline text-sm">
              View Site →
            </Link>
          </div>
        </header>

        {/* Page Content */}
        <div className="p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
